import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion, AnimatePresence } from "framer-motion";
import {
  Zap,
  Sparkles,
  Bell,
  Lightbulb,
  Layers,
  CheckSquare,
  Check,
  CheckCheck,
  AlertTriangle,
  Plus,
  Minus,
} from "lucide-react";
import "./OrgIntelligence.css";
import PublicHeader from "../../components/PublicHeader/PublicHeader";
import Testimonials from "../../components/Testimonials/Testimonials";
import ReadyToTransform from "../../components/ReadyToTransform/ReadyToTransform";
import Footer from "../../components/Footer/Footer";

const TITLE = "Org Intelligence Software | AI Work Pattern Analysis | TeamGrid";
const DESCRIPTION =
  "Analyze work patterns with AI driven org intelligence. Detect strengths, identify issues early and get smart recommendations to improve team performance.";
const CANONICAL = "https://teamgrid.ai/features/org-intelligence";
const OG_IMAGE = "https://teamgrid.ai/og/og-home.webp";

const OrgIntelligence = () => {
  const navigate = useNavigate();
  const [faqOpen, setFaqOpen] = useState({ 0: true });
  const faqCardRef = useRef(null);

  const toggleFaq = (i) => {
    const opening = !faqOpen[i];
    setFaqOpen((prev) => ({ ...prev, [i]: !prev[i] }));
    if (opening && faqCardRef.current) {
      setTimeout(() => {
        faqCardRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 150);
    }
  };

  return (
    <div className="org-intelligence-container">
      <Helmet>
        <title>{TITLE}</title>
        <meta name="description" content={DESCRIPTION} />
        <link rel="canonical" href={CANONICAL} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={CANONICAL} />
        <meta property="og:title" content={TITLE} />
        <meta property="og:description" content={DESCRIPTION} />
        <meta property="og:image" content={OG_IMAGE} />
        <meta property="og:site_name" content="TeamGrid" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={CANONICAL} />
        <meta name="twitter:title" content={TITLE} />
        <meta name="twitter:description" content={DESCRIPTION} />
        <meta name="twitter:image" content={OG_IMAGE} />
      </Helmet>
      <div className="relative min-h-screen overflow-hidden bg-[#F3F4F6]">
        <PublicHeader />
        <div className="pt-16 sm:pt-18 md:pt-20">
          {/* Background: grey above Customer Success */}
          <div
            className="absolute inset-0"
            style={{
              background: "#F3F4F6",
            }}
          ></div>

          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            {/* Hero: full-bleed gradient - inner content aligned with header */}
            <div
              className="relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] w-screen pt-16 sm:pt-18 md:pt-20 pb-10 sm:pb-12 md:pb-14 px-4 sm:px-6 lg:px-8"
              style={{
                background:
                  "radial-gradient(141.42% 141.42% at 0% 0%, rgba(230, 242, 255, 0.5) 0%, rgba(233, 252, 250, 0) 50%), radial-gradient(141.42% 141.42% at 100% 100%, rgba(240, 247, 255, 0.5) 0%, rgba(240, 247, 255, 0) 50%), #FFFFFF",
              }}
            >
              <div className="max-w-7xl mx-auto">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  className="mb-0 text-center"
                >
                  <div className="flex items-center justify-center gap-3 mb-6">
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#00D1C1]/10 border border-[#00D1C1]/20">
                      <Zap className="w-4 h-4 flex-shrink-0 text-[#00D1C1]" />
                      <span className="text-sm font-semibold uppercase tracking-wide text-[#00D1C1]">
                        AI INTELLIGENCE
                      </span>
                    </div>
                  </div>
                  <h1
                    className="font-black tracking-tight text-gray-900 mb-4"
                    style={{
                      fontSize: "clamp(2.5rem, 5vw, 62px)",
                      lineHeight: "clamp(50px, 8vw, 72px)",
                    }}
                  >
                    Org <span className="text-[#00D1C1]">Intelligence</span>
                  </h1>
                  <p className="text-sm md:text-base text-gray-500 max-w-2xl mx-auto leading-relaxed text-center">
                    Automatic analysis of work patterns, behavior detection, and
                    smart recommendations to help your team perform at their
                    best.
                  </p>
                </motion.div>
              </div>
            </div>

            {/* Main Content - full-bleed white bg, content aligned with header; no bottom padding so Testimonials sits flush */}
            <section className="relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] w-screen pt-12 pb-0 bg-white">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
                {/* Understand Work Like Never Before + AI Analysis Summary card */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                  className="mb-0"
                >
                  <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start mb-0">
                    {/* Left: Title, copy, features (circular teal outline icons), CTAs */}
                    <div>
                      <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 leading-tight">
                        Understand Work Like
                        <br />
                        Never Before
                      </h2>
                      <p className="text-gray-700 mb-8 max-w-xl leading-relaxed">
                        TeamGrid's AI analyzes thousands of data points to
                        surface patterns you'd never find manually. Get
                        actionable recommendations that actually improve
                        productivity.
                      </p>
                      <div className="space-y-5 mb-8">
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0 w-10 h-10 rounded-full border-2 border-[#00D1C1] bg-[#00D1C1]/10 flex items-center justify-center">
                            <Sparkles
                              className="w-5 h-5 text-[#00D1C1]"
                              strokeWidth={1.5}
                            />
                          </div>
                          <div>
                            <h3 className="font-bold text-gray-900 mb-0.5">
                              Strengths Detection
                            </h3>
                            <p className="text-sm text-gray-600">
                              Automatically identify what your team does
                              exceptionally well.
                            </p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0 w-10 h-10 rounded-full border-2 border-[#00D1C1] bg-[#00D1C1]/10 flex items-center justify-center">
                            <Bell
                              className="w-5 h-5 text-[#00D1C1]"
                              strokeWidth={1.5}
                            />
                          </div>
                          <div>
                            <h3 className="font-bold text-gray-900 mb-0.5">
                              Issue Identification
                            </h3>
                            <p className="text-sm text-gray-600">
                              Catch bottlenecks and problems before they impact
                              deadlines.
                            </p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0 w-10 h-10 rounded-full border-2 border-[#00D1C1] bg-[#00D1C1]/10 flex items-center justify-center">
                            <Lightbulb
                              className="w-5 h-5 text-[#00D1C1]"
                              strokeWidth={1.5}
                            />
                          </div>
                          <div>
                            <h3 className="font-bold text-gray-900 mb-0.5">
                              Smart Recommendations
                            </h3>
                            <p className="text-sm text-gray-600">
                              Actionable steps based on what works for similar
                              teams.
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-4">
                        <motion.button
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => navigate("/book-demo")}
                          className="px-6 py-3 rounded-lg bg-[#00D1C1] text-white font-semibold hover:bg-[#00b8a8] transition-colors"
                        >
                          Try AI Insights
                        </motion.button>
                        <button
                          type="button"
                          onClick={() => navigate("/book-demo")}
                          className="text-gray-900 font-medium hover:underline"
                        >
                          Watch Demo
                        </button>
                      </div>
                    </div>
                    {/* Right: AI Analysis Summary card - white, shadow, teal border accent */}
                    <div className="bg-white rounded-2xl p-8 shadow-[0_4px_24px_rgba(0,0,0,0.08)] border border-gray-100 overflow-hidden min-w-0">
                      {/* Header: sparkle icon + title + subtitle */}
                      <div className="flex items-start gap-3 mb-4">
                        <Sparkles
                          className="w-5 h-5 text-[#00D1C1] flex-shrink-0 mt-0.5"
                          strokeWidth={1.5}
                        />
                        <div>
                          <h3 className="font-bold text-gray-900">
                            AI Analysis Summary
                          </h3>
                          <p className="text-sm text-gray-500 mt-0.5">
                            Engineering Team • This Month
                          </p>
                        </div>
                      </div>
                      <div className="border-b border-gray-200 mb-4" />

                      {/* Strengths and Issues: rectangular cards, side-by-side */}
                      <div className="flex flex-row flex-wrap items-stretch justify-center gap-4 mb-4 min-w-0">
                        <div
                          className="flex flex-col items-start flex-1 min-w-[240px] max-w-[320px] rounded-lg p-4 gap-3"
                          style={{
                            background: "rgba(247, 247, 247, 0.7)",
                            border: "1px solid #EDF4FF",
                          }}
                        >
                          <div className="flex flex-row justify-between items-center w-full gap-2">
                            <span
                              className="text-sm font-bold leading-5"
                              style={{ color: "#34A851" }}
                            >
                              Strengths
                            </span>
                            <span
                              className="w-6 h-6 rounded-full bg-[#E0FFFC] text-xs font-bold flex items-center justify-center flex-shrink-0"
                              style={{ color: "#34A851" }}
                            >
                              5
                            </span>
                          </div>
                          <ul
                            className="flex flex-col gap-2.5 w-full text-xs leading-4"
                            style={{ color: "rgba(0,0,0,0.6)" }}
                          >
                            <li className="flex flex-row items-center gap-2">
                              <Check
                                className="w-3.5 h-3.5 flex-shrink-0"
                                style={{ color: "#34A851" }}
                                strokeWidth={2.5}
                              />
                              <span>Deep technical expertise in backend</span>
                            </li>
                            <li className="flex flex-row items-center gap-2">
                              <Check
                                className="w-3.5 h-3.5 flex-shrink-0"
                                style={{ color: "#34A851" }}
                                strokeWidth={2.5}
                              />
                              <span>Mid-week productivity peaks at 87%</span>
                            </li>
                            <li className="flex flex-row items-center gap-2">
                              <Check
                                className="w-3.5 h-3.5 flex-shrink-0"
                                style={{ color: "#34A851" }}
                                strokeWidth={2.5}
                              />
                              <span>Zero idle time during core hours</span>
                            </li>
                          </ul>
                        </div>
                        <div
                          className="flex flex-col items-start flex-1 min-w-[240px] max-w-[320px] rounded-lg p-4 gap-3"
                          style={{
                            background: "rgba(247, 247, 247, 0.7)",
                            border: "1px solid #EDF4FF",
                          }}
                        >
                          <div className="flex flex-row justify-between items-center w-full gap-2">
                            <span
                              className="text-sm font-bold leading-5"
                              style={{ color: "#F43F5E" }}
                            >
                              Issues
                            </span>
                            <span
                              className="w-6 h-6 rounded-full bg-[#FFEDD5] text-xs font-bold flex items-center justify-center flex-shrink-0"
                              style={{ color: "#F43F5E" }}
                            >
                              4
                            </span>
                          </div>
                          <ul
                            className="flex flex-col gap-2.5 w-full text-xs leading-4"
                            style={{ color: "rgba(0,0,0,0.6)" }}
                          >
                            <li className="flex flex-row items-center gap-2">
                              <AlertTriangle
                                className="w-3.5 h-3.5 flex-shrink-0"
                                style={{ color: "#F2673A" }}
                                strokeWidth={2}
                              />
                              <span>Monday productivity -23% vs avg</span>
                            </li>
                            <li className="flex flex-row items-center gap-2">
                              <AlertTriangle
                                className="w-3.5 h-3.5 flex-shrink-0"
                                style={{ color: "#F2673A" }}
                                strokeWidth={2}
                              />
                              <span>Documentation gaps in sprints</span>
                            </li>
                            <li className="flex flex-row items-center gap-2">
                              <AlertTriangle
                                className="w-3.5 h-3.5 flex-shrink-0"
                                style={{ color: "#F2673A" }}
                                strokeWidth={2}
                              />
                              <span>47 app switches/hr during focus</span>
                            </li>
                          </ul>
                        </div>
                      </div>

                      {/* AI Recommendation: teal background fill, lightbulb + title, green highlight in text */}
                      <div className="rounded-xl p-4 bg-[#00D1C1]/10 border border-[#00D1C1]/20">
                        <div className="flex items-start gap-3">
                          <Lightbulb
                            className="w-5 h-5 text-black flex-shrink-0 mt-0.5"
                            strokeWidth={1.5}
                          />
                          <div>
                            <h4 className="text-sm font-bold text-gray-900 mb-1.5">
                              AI Recommendation
                            </h4>
                            <p className="text-sm text-gray-700 leading-relaxed">
                              Move Monday standups to 9:30 AM. Teams with early
                              standups show{" "}
                              <span className="font-semibold text-green-600">
                                18% higher Monday productivity
                              </span>
                              . This change alone could recover ~4 hours/week.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
              {/* Grey bg: three feature cards + faint diagonal stripe at top */}
              <div className="relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] w-screen bg-[#F3F4F6] pt-12 pb-12 md:pb-16 mt-8 overflow-hidden">
                {/* Faint light blue diagonal stripes at top edge */}
                <div
                  className="absolute top-0 left-0 right-0 h-3"
                  style={{
                    background:
                      "repeating-linear-gradient(-45deg, transparent, transparent 3px, rgba(147, 197, 253, 0.12) 3px, rgba(147, 197, 253, 0.12) 6px)",
                  }}
                />
                <div className="max-w-7xl mx-auto px-6 md:px-8 relative">
                  <div className="grid md:grid-cols-3 gap-6">
                    <motion.div
                      initial={{ opacity: 0, y: 16 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: 0.2 }}
                      className="bg-white rounded-2xl p-6 shadow-[0_1px_3px_rgba(0,0,0,0.05),0_4px_12px_rgba(0,0,0,0.04)] text-left"
                    >
                      <div className="w-12 h-12 rounded-xl bg-[#CCFBF1] flex items-center justify-center mb-4">
                        <Layers
                          className="w-6 h-6 text-[#0D9488]"
                          strokeWidth={1.75}
                        />
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">
                        Pattern Recognition
                      </h3>
                      <p className="text-sm text-gray-500 leading-relaxed">
                        AI identifies work patterns across your entire
                        organization automatically.
                      </p>
                    </motion.div>
                    <motion.div
                      initial={{ opacity: 0, y: 16 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: 0.3 }}
                      className="bg-white rounded-2xl p-6 shadow-[0_1px_3px_rgba(0,0,0,0.05),0_4px_12px_rgba(0,0,0,0.04)] text-left"
                    >
                      <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 overflow-hidden flex-shrink-0">
                        <img
                          src="/Overlay_1.webp"
                          alt="Predictive Analytics"
                          className="w-12 h-12 object-cover rounded-xl"
                        />
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">
                        Predictive Analytics
                      </h3>
                      <p className="text-sm text-gray-500 leading-relaxed">
                        Forecast productivity trends and catch issues before
                        they happen.
                      </p>
                    </motion.div>
                    <motion.div
                      initial={{ opacity: 0, y: 16 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: 0.4 }}
                      className="bg-white rounded-2xl p-6 shadow-[0_1px_3px_rgba(0,0,0,0.05),0_4px_12px_rgba(0,0,0,0.04)] text-left"
                    >
                      <div className="w-12 h-12 rounded-xl bg-[#CCFBF1] flex items-center justify-center mb-4">
                        <CheckCheck
                          className="w-6 h-6 text-[#00D1C1]"
                          strokeWidth={1.75}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">
                        Actionable Steps
                      </h3>
                      <p className="text-sm text-gray-500 leading-relaxed">
                        Get specific recommendations you can implement
                        immediately.
                      </p>
                    </motion.div>
                  </div>
                </div>
              </div>
            </section>

            {/* Testimonials Section - Customer Success */}
            <Testimonials />

            {/* FAQ Section */}
            <div className="py-16 px-4 sm:px-6 md:px-8 bg-[#F3F4F6]">
              <div className="max-w-4xl mx-auto">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  className="text-center mb-12"
                >
                  <span className="inline-block px-5 py-2.5 rounded-full text-xs font-semibold uppercase tracking-wide text-[#0D9488] bg-[#CCFBF1] border border-[#99F6E4] mb-6">
                    GOT QUESTIONS?
                  </span>
                  <h2 className="text-[24px] sm:text-[28px] md:text-[32px] lg:text-[36px] font-bold text-black mb-4">
                    Frequently Asked Questions
                  </h2>
                  <p className="text-lg text-gray-600">
                    Everything you need to know about TeamGrid for
                    organizational intelligence
                  </p>
                </motion.div>

                <div className="space-y-4 w-full max-w-[720px] mx-auto">
                  {[
                    {
                      question: "What insights does Org Intelligence deliver?",
                      answer:
                        "Org Intelligence aggregates team data to spotlight organizational patterns, workload balance, performance trends, and opportunities for strategic improvements at scale.",
                    },
                  ].map((faq, index) => (
                    <motion.div
                      key={index}
                      ref={index === 0 ? faqCardRef : null}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: 0.1 * index }}
                      className="w-full rounded-xl p-6 cursor-pointer bg-white"
                      style={{
                        border: faqOpen[index]
                          ? "1px solid #28B4AE"
                          : "1px solid #D1D5DB",
                        transition: "border-color 0.15s ease-in-out",
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => toggleFaq(index)}
                        className="w-full flex items-center justify-between text-left"
                      >
                        <h3
                          className="text-left font-bold text-[#1A1A1A] flex-1"
                          style={{
                            fontFamily: "Inter, sans-serif",
                            fontSize: 16,
                            lineHeight: "26px",
                          }}
                        >
                          {faq.question}
                        </h3>
                        <span className="flex-shrink-0">
                          {faqOpen[index] ? (
                            <Minus className="w-5 h-5 text-[#111111]" />
                          ) : (
                            <Plus className="w-5 h-5 text-[#111111]" />
                          )}
                        </span>
                      </button>
                      <AnimatePresence initial={false}>
                        {faqOpen[index] && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3, ease: "easeInOut" }}
                            className="overflow-hidden"
                          >
                            <p
                              className="mt-4"
                              style={{
                                fontFamily: "Inter, sans-serif",
                                fontWeight: 400,
                                fontSize: 16,
                                lineHeight: "26px",
                                color: "#374151",
                              }}
                            >
                              {faq.answer}
                            </p>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>

            <ReadyToTransform />

            <Footer />
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrgIntelligence;
